<script setup lang="ts">
import { formatNumberFullTruncate } from '@/helpers/utils'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'
import { usePlayer, usePlayerProfit } from '@/composables/game/player'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import MultiTapIcon from '@/components/icons/MultiTapIcon.vue'
import EnergyIcon from '@/components/icons/EnergyIcon.vue'
import { ref, watch } from 'vue'
import BoostActionModal from '@/components/modals/BoostActionModal.vue'
import { useConfig } from '@/composables/game/config'
import { twMerge } from 'tailwind-merge'
import { T } from '@tolgee/vue'
import FlashIconGIF from '@/components/icons/FlashIconGIF.vue'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import BoostShop from '@/components/BoostShop.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'

enum Boost {
	FULL_ENERGY,
	MULTITAP,
	ENERGY_LIMIT
}

const {
  canUseRefillBooster,
  nextRefillBoosterAvailableIn,
  player,
} = usePlayer()

const { config } = useConfig()
const {
  activeBoostersCount,
  hasDoubleProfit,
  formattedDoubleProfitCountdown,
  hasExtraOfflineProfit,
  formattedExtraOfflineProfit,
} = usePlayerProfit()

const showActionModal = ref(false)
const selectedBoost = ref<Boost>(Boost.FULL_ENERGY)

function getTimeToShow() {
  if ((nextRefillBoosterAvailableIn.value) >= 3600) {
    return `${Math.floor(nextRefillBoosterAvailableIn.value / 3600)}h left`
  } else if (nextRefillBoosterAvailableIn.value >= 60) {
    return `${Math.floor(nextRefillBoosterAvailableIn.value / 60)}m left`
  } else {
    return `${Math.floor(nextRefillBoosterAvailableIn.value)}s left`
  }
}

function selectAction(boost: Boost) {
  selectedBoost.value = boost
  showActionModal.value = true
}

watch(canUseRefillBooster, () => {
}, { immediate: true })
</script>

<template>
  <div class="flex flex-col px-4 py-6 items-center w-full relative">
    <StarsBalancePill
      class="absolute top-6 right-4"
      wrapper-class="items-end"
    />
    <img
      class="size-[120px] object-cover"
      src="@/assets/images/gifs/rocket.gif"
    >
    <h1 class="text-light-white font-bold text-[32px] leading-[32px] mt-4 uppercase bounce-in-right">
      <T
        key-name="boost.title"
        default-value="Boost"
      />
    </h1>
    <p class="text-light-white/70 text-base leading-6 font-medium -tracking-[0.32px]">
      <T
        key-name="boost.subtitle"
        default-value="Get boosters to supercharge your progress"
      />
    </p>

    <div class="mt-8 w-full flex flex-col gap-y-6">
      <div class="flex flex-col gap-y-3">
        <div class="text-[22px] leading-7 font-semibold text-light-white -tracking-[0.44px]">
          <T
            key-name="boost.trading_per_hour"
            default-value="Trading per hour (TPH)"
          />
        </div>
        <BoostShop />
      </div>
    </div>

    <div class="mt-6 flex flex-col w-full gap-y-3">
      <div class="text-[22px] leading-7 font-semibold text-light-white -tracking-[0.44px]">
        <T
          key-name="boost.tapping_boosters"
          default-value="Tapping boosters"
        />
      </div>
      <div
        :class="twMerge('flex bg-dark-bottom-layer h-[76px] w-full py-2 pl-3 pr-4 rounded-xl justify-between items-center',
                        !canUseRefillBooster && 'pointer-events-none')"
        @click="selectAction(Boost.FULL_ENERGY)"
      >
        <div class="flex gap-x-3 items-center min-w-0">
          <FlashIconGIF class="w-12 h-12 min-w-[48px] scale-in" />
          <div
            class="flex flex-col gap-y-0.5 text-white min-w-0"
          >
            <p class="text-base font-semibold -tracking-[0.32px]">
              <T
                key-name="boost.full_energy"
                default-value="Full energy"
              />
            </p>
            <p
              class="text-xs font-medium -tracking-[0.24px] text-white/60"
              :class="{'opacity-40': !canUseRefillBooster}"
            >
              <T
                key-name="boost.energy_available"
                default-value="{available} / {max} available"
                :params="{ available: player.refillBooster.available, max: player.refillBooster.max}"
              />
            </p>
          </div>
        </div>
        <ChevronRightIcon
          v-if="canUseRefillBooster|| (player.refillBooster.available === player.refillBooster.max && !nextRefillBoosterAvailableIn)"
          class="ml-6 size-6 min-w-[24px] text-white"
        />
        <div
          v-else-if="nextRefillBoosterAvailableIn"
          class="flex gap-x-1 items-center opacity-60"
        >
          <ClockIcon class="size-4 min-w-[16px]" />
          <p class="text-sm font-medium">
            {{ getTimeToShow() }}
          </p>
        </div>
      </div>
      <div
        class="flex bg-dark-bottom-layer h-[76px] w-full py-2 pl-3 pr-4 rounded-xl justify-between items-center"
        @click="selectAction(Boost.MULTITAP)"
      >
        <div class="flex gap-x-3 items-center min-w-0">
          <MultiTapIcon class="size-12 min-w-[48px] scale-in" />
          <div
            class="flex flex-col gap-y-0.5"
          >
            <div class="text-base font-semibold -tracking-[0.32px]">
              <T
                key-name="boost.boosters.multitap"
                default-value="Multitap"
              />
            </div>
            <div class="flex items-center gap-x-3">
              <div
                class="flex items-center gap-x-1"
              >
                <CoinIcon class="w-[10px] h-4 min-w-[10px]" />
                <span class="font-semibold text-xs -tracking-[0.24px]">{{
                  formatNumberFullTruncate(config.tap.tap.price)
                }} </span>
              </div>
              <div class="w-[1px] h-4 bg-white/20" />
              <p class="font-medium text-xs leading-6 -tracking-[0.24px]">
                <T
                  key-name="booster.level"
                  default-value="lvl {level}"
                  :params="{ level: config.tap.tap.level }"
                />
              </p>
            </div>
          </div>
        </div>
        <ChevronRightIcon
          class="ml-6 size-6 min-w-[24px] text-white"
        />
      </div>
      <div
        class="flex bg-dark-bottom-layer h-[76px] w-full py-2 pl-3 pr-4 rounded-xl justify-between items-center"
        @click="selectAction(Boost.ENERGY_LIMIT)"
      >
        <div class="flex gap-x-3 items-center min-w-0">
          <EnergyIcon class="w-12 h-12 min-w-[48px] scale-in" />
          <div
            class="flex flex-col gap-y-0.5"
          >
            <p class="text-base font-semibold -tracking-[0.32px]">
              <T
                key-name="boost.boosters.energy_limit"
                default-value="Energy Limit"
              />
            </p>
            <div class="flex items-center gap-x-3">
              <div
                class="flex items-center gap-x-1"
              >
                <CoinIcon class="w-[10px] h-4 min-w-[10px]" />
                <span class="font-semibold text-xs -tracking-[0.24px]">{{
                  formatNumberFullTruncate(config.tap.energy.price)
                }} </span>
              </div>
              <div class="w-[1px] h-4 bg-white/20" />
              <p class="font-medium text-xs leading-6 -tracking-[0.24px]">
                <T
                  key-name="booster.level"
                  default-value="lvl {level}"
                  :params="{ level: config.tap.energy.level }"
                />
              </p>
            </div>
          </div>
        </div>
        <ChevronRightIcon
          class="ml-6 size-6 min-w-[24px] text-white"
        />
      </div>
    </div>

    <div class="w-full bg-dark-top-most-layer h-px my-6" />

    <div class="w-full flex flex-col gap-y-3">
      <div class="flex items-center gap-x-1.5">
        <div class="text-[22px] leading-7 font-semibold text-light-white -tracking-[0.44px] ">
          <T
            key-name="boost.active_boosters"
            default-value="Active boosters"
          />
        </div>
        <div
          v-if="activeBoostersCount"
          class="mt-[3px] w-4 h-4 min-w-[16px] rounded-full bg-green-primary shadow-boost-counter flex items-center justify-center"
        >
          <span class="text-[10px] leading-4 font-bold text-tropee-black">{{ activeBoostersCount }}</span>
        </div>
      </div>

      <div
        v-if="!activeBoostersCount"
        class="text-base font-semibold -tracking-[0.32px]"
      >
        <T
          key-name="boost.no_active_boost1"
          default-value=" You have no active boosters at the moment."
        />
        <br>
        <T
          key-name="boost.no_active_boost2"
          default-value="Get one now to accelerate your growth!"
        />
      </div>
      <div
        v-else
        class="flex flex-col gap-y-3"
      >
        <div
          v-if="hasDoubleProfit"
          class="active-boost-box"
        >
          <img
            src="@/assets/images/prize-cards/tph2x.png"
            class="w-9 min-w-[36px] h-auto"
          >
          <div class="text-base -tracking-[0.32px] font-semibold">
            <T
              key-name="boost.trading_per_hour_2x"
              default-value="Trading per hour x2"
            />
          </div>
          <div class="ml-auto text-base font-medium -tracking-[0.32px]">
            {{ formattedDoubleProfitCountdown(true) }}
          </div>
        </div>

        <div
          v-if="hasExtraOfflineProfit"
          class="active-boost-box"
        >
          <img
            src="@/assets/images/prize-cards/offline-profit.png"
            class="w-9 min-w-[36px] h-auto"
          >
          <div class="text-base -tracking-[0.32px] font-semibold">
            <T
              key-name="boost.extra_offline_profit"
              default-value="Extra offline profit"
            />
          </div>
          <div class="ml-auto text-base font-medium -tracking-[0.32px]">
            {{ formattedExtraOfflineProfit }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <BoostActionModal
    v-if="showActionModal"
    :show="showActionModal"
    :selected-boost="selectedBoost"
    @hide="showActionModal = false"
  />
</template>

<style scoped lang="scss">
.shadow-boost-counter {
	box-shadow: 0px 0px 4px 0px #3B9A7C;
}

.active-boost-box {
	@apply rounded-xl bg-dark-bottom-layer py-3 px-4 flex items-center gap-x-3 h-[60px];
}
</style>
